import { p as proxyCustomElement, H, c as createEvent, h } from './p-bfa0dc67.js';
import { c as classNames } from './p-df8c8ec2.js';
import { i as inheritAriaAttributes } from './p-198613ef.js';
import { l as labelId, d as descriptionId, L as Label } from './p-8097ff18.js';
import { V as Validation, m as messageId } from './p-319941a1.js';
import { d as defineCustomElement$2 } from './p-fe794afe.js';

const biInputFieldCss = ".sr-only{position:absolute;height:1px;width:1px;padding:0;margin:-1px;overflow:hidden;border:0;left:-9999px;text-indent:-5000px;clip:rect(1px, 1px, 1px, 1px);}*,*:before,*:after{box-sizing:border-box}html,body,div,span,object,iframe,figure,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,code,em,img,small,strike,strong,sub,sup,tt,b,u,i,ol,ul,li,fieldset,form,label,table,caption,tbody,tfoot,thead,tr,th,td,main,canvas,embed,footer,header,nav,section,button,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;text-size-adjust:none}footer,header,nav,section,main{display:block}body{line-height:1.5}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:before,blockquote:after,q:before,q:after{content:\"\";content:none}table{border-collapse:collapse;border-spacing:0}input{-webkit-appearance:none;appearance:none;border-radius:0}:host(:focus){outline:none !important;border:none !important}.label-wrapper{display:flex;flex-direction:column;gap:4px;height:fit-content;margin-bottom:8px}.label-wrapper .label{color:#414651;font-size:1rem;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;height:fit-content}.label-wrapper .label.fontStyle--regular{font-weight:500}.label-wrapper .label.fontStyle--bold{font-weight:700}.label-wrapper .required{color:#8c0a08;margin-left:8px}.label-wrapper .label+.label{font-family:\"museo-sans\", arial, sans-serif;color:#717680;font-size:0.75rem;line-height:1.5}.label-wrapper.dark .label{color:whitesmoke}.label-wrapper.dark .required{color:#ec9a98}.label-wrapper.dark .label+.label{color:#e9eaeb}.label-wrapper.noMargin{margin-bottom:0}.validation-message{font-size:1rem;color:#8c0a08;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;width:100%;height:1.5rem;margin-top:12px;margin-bottom:12px;display:block}.validation-message.dark{color:#ec9a98}.validation-message.noMargin{margin:0}:host(:focus){outline:none;border:none}div.input-field-wrapper{position:relative;display:flex;border:1.5px solid #01417b;border-radius:4px;width:100%;max-width:520px;min-width:256px;background-color:white;color:#414651}div.input-field-wrapper.stretch{max-width:none}div.input-field-wrapper.dark{color-scheme:dark;border-color:#78c2ff;background-color:#002341;color:whitesmoke}div.input-field-wrapper:hover{background-color:#cfebff;border-color:#01417b}div.input-field-wrapper:hover.dark{background-color:rgba(1, 65, 127, 0.2509803922);border-color:#78c2ff}div.input-field-wrapper.warning{border-color:#8c0a08}div.input-field-wrapper.warning:hover{border-color:#1a77e9}div.input-field-wrapper.warning.dark{border-color:#ec9a98}div.input-field-wrapper:has(:disabled){background-color:unset;border-color:#01417b}div.input-field-wrapper:has(:disabled).dark{border-color:#e9eaeb}div.input-field-wrapper:has(:focus-visible){border-radius:4px;outline-offset:2px;outline:1.5px solid var(--bi-primary-50);box-shadow:0 0 0 2px white}div.input-field-wrapper .icon{display:flex;align-items:center;padding-right:12px;color:#8c0a08}div.input-field-wrapper.dark .icon{color:#ec9a98}div.input-field-wrapper ::slotted([slot=start-adornment]),div.input-field-wrapper ::slotted([slot=end-adornment]){align-self:center}div.input-field-wrapper ::slotted([slot=start-adornment]){margin-left:12px !important}div.input-field-wrapper ::slotted([slot=end-adornment]){margin-right:12px !important}input,textarea{font-family:\"museo-sans\", arial, sans-serif;font-size:1rem;line-height:1.5;padding:12px;position:relative;outline:none;background:transparent;border:none;width:100%}input[rows],textarea[rows]{resize:none}div.input-wrapper{display:flex;flex-direction:column}div.input-wrapper:has(:disabled){opacity:0.3}";
const BiInputFieldStyle0 = biInputFieldCss;

const BiInputField$1 = /*@__PURE__*/ proxyCustomElement(class BiInputField extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.change = createEvent(this, "change", 7);
        this.blur = createEvent(this, "blur", 7);
        this.input = createEvent(this, "input", 7);
        this.internals = this.attachInternals?.();
        this.inheritedAttributes = {};
        // The "name" property is reflected as an attribute to ensure compatibility with native form submission.
        // In the React wrapper, all props are synced as properties on the element ref, so reflecting "name" as an attribute ensures it is properly handled in the form submission process.
        /** You can set the initial value of the input field. Don't set this if you're also setting the `value`-prop */
        this.defaultValue = '';
        /** Marks the input field as required. */
        this.required = false;
        /** Marks the input field as disabled. */
        this.disabled = false;
        /** The type of the input field. */
        this.type = 'text';
        /** Specifies whether the input can be autofilled by the browser */
        this.autocomplete = 'off';
        /** The size of the input field. Size `normal` renders a normal input field, while `long` will render a textarea. */
        this.size = 'normal';
        /** Is the component displayed on a dark background. */
        this.dark = false;
        /** Stretch the input to use full available width. */
        this.stretch = false;
        /** Handles wrapping behaviour of elements. */
        this.wrap = 'soft';
        /** Controls whether the textarea is resizable and in which direction. */
        this.resize = 'none';
        /**
         * If `true`, the component will indicate an error.
         * @default false
         */
        this.error = false;
        this.onChange = (e) => {
            this.change.emit(e);
        };
        this.onBlur = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.blur.emit(e);
        };
        this.onInput = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            const target = e.target;
            this.value = target.value; // triggers @Watch('value')
            this.input.emit(e);
        };
    }
    onValueChange(newValue) {
        this.internals?.setFormValue(newValue);
    }
    componentWillLoad() {
        if (this.value !== undefined) {
            this.defaultValue = this.value;
        }
        else {
            this.value = this.defaultValue ?? '';
        }
        this.inheritedAttributes = inheritAriaAttributes(this.el);
    }
    formResetCallback() {
        this.internals?.setFormValue(this.defaultValue);
        this.value = this.defaultValue;
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    componentDidLoad() {
        this.internals?.setFormValue(this.value);
    }
    componentDidRender() {
        this.internals?.setValidity(this.inputElement.validity, this.inputElement.validationMessage, this.inputElement);
    }
    render() {
        const id = 'input-field';
        return (h("div", { key: '661606220bb5b44fbf72ad78130d1741d533231b', class: classNames('input-wrapper', this.stretch && 'stretch') }, h(Label, { key: '0723ce2312ac70fb5d7a747b8d62697e1c71530e', htmlFor: id, label: this.label, helptext: this.helptext, required: this.required, disabled: this.disabled, dark: this.dark }), h("div", { key: '4dc7ca6d6662a923d0066a7cbeb249d42111748e', class: classNames('input-field-wrapper', this.error && 'warning', this.stretch && 'stretch', this.dark && 'dark') }, h("slot", { key: '72d271aeeb259c282a31cb1eccd4403fa82e0e7c', name: 'start-adornment' }), this.size === 'normal' ? (h("input", { "aria-labelledby": labelId, "aria-describedby": `${descriptionId} ${messageId}`, "aria-invalid": String(this.error), id: id, class: classNames(this.stretch && 'stretch'), ref: (el) => (this.inputElement = el), onInput: this.onInput, onChange: this.onChange, onBlur: this.onBlur, name: this.name, value: this.value, disabled: this.disabled, required: this.required, placeholder: this.placeholder, maxlength: this.maxLength, minlength: this.minLength, autocomplete: this.autocomplete, type: this.type, ...this.inheritedAttributes })) : (this.size === 'long' && (h("textarea", { "aria-describedby": `${descriptionId} ${messageId}`, "aria-invalid": String(this.error), id: id, class: classNames(this.stretch && 'stretch'), style: { resize: this.resize }, ref: (el) => (this.inputElement = el), onInput: this.onInput, onChange: this.onChange, onBlur: this.onBlur, name: this.name, value: this.value, disabled: this.disabled, required: this.required, placeholder: this.placeholder, maxlength: this.maxLength, minlength: this.minLength, autocomplete: this.autocomplete, rows: this.rows, ...this.inheritedAttributes }))), h("slot", { key: 'ca381aca87e22ee8a60f2662203e1a0ab341ea64', name: 'end-adornment' }), this.error && h("div", { key: 'b3f2bb61530a2f59cca57dd45bc25614ee766a6b', class: 'icon' }, h("bi-icon-warning", { key: '02b2f0a9e57298f1c164563577989862961cc55f', size: 'medium', "aria-hidden": 'true' }))), h(Validation, { key: '1a6a07ac830da51e3744c9a55c318c5b00b36b4b', dark: this.dark, visible: this.error, message: this.errorMessage })));
    }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    get el() { return this; }
    static get watchers() { return {
        "value": ["onValueChange"]
    }; }
    static get style() { return BiInputFieldStyle0; }
}, [81, "bi-input-field", {
        "label": [1],
        "helptext": [1],
        "name": [513],
        "defaultValue": [1, "default-value"],
        "value": [1025],
        "required": [4],
        "disabled": [4],
        "type": [1],
        "autocomplete": [1],
        "size": [1],
        "rows": [2],
        "dark": [4],
        "placeholder": [1],
        "minLength": [2, "min-length"],
        "maxLength": [2, "max-length"],
        "pattern": [1],
        "stretch": [4],
        "wrap": [1],
        "resize": [1],
        "errorMessage": [1, "error-message"],
        "error": [4]
    }, undefined, {
        "value": ["onValueChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bi-input-field", "bi-icon-warning"];
    components.forEach(tagName => { switch (tagName) {
        case "bi-input-field":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BiInputField$1);
            }
            break;
        case "bi-icon-warning":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const BiInputField = BiInputField$1;
const defineCustomElement = defineCustomElement$1;

export { BiInputField, defineCustomElement };
